import * as React from 'react';
import {
    ShimmerElementsGroup,
    ShimmerElementType as ElemType,
    Shimmer,
    IShimmerStyles,
    IShimmerProps,
} from '@fluentui/react/lib/Shimmer';
import { useElementSizeTracker } from 'owa-react-hooks/lib/useElementSizeTracker';
import { mergeStyles } from '@fluentui/react/lib/Styling';

import classNames from 'classnames';
import styles from './AgendaEventsLoadingListView.scss';

const DEFAULT_NUM_SHIMMER_ELEMENTS = 8;
const DEFAULT_ITEM_HEIGHT = 64;
const DEFAULT_ITEM_SPACING = 0;

/**
 * There are 4 "blocks" (sections of grey boxes that shimmer)
 * The first block is a thin vertical line representing the free-busy bar
 * The second block is a larger square representing the persona circle for the organizer
 * The third block is two grey lines of "text"
 * The fourth block is an invisible spacer taking up the remainder of the width
 */
const firstBlockWidth = 32;
const secondBlockWidth = 65;
const thirdBlockWidth = 139;
const fourthBlockWidth = 84;

const defaultTotalWidth = firstBlockWidth + secondBlockWidth + thirdBlockWidth + fourthBlockWidth;

export interface AgendaEventsLoadingListViewProps {
    isDataLoaded: boolean;
    className?: string;
    shimmerClassName?: string;
    shimmerWrapperClassName?: string;
    numShimmerElements?: number;
    itemHeight?: number;
    itemSpacing?: number;
}

export const AgendaEventsLoadingListView: React.FunctionComponent<AgendaEventsLoadingListViewProps> = props => {
    const {
        isDataLoaded,
        className,
        shimmerClassName,
        shimmerWrapperClassName,
        numShimmerElements = DEFAULT_NUM_SHIMMER_ELEMENTS,
        itemHeight = DEFAULT_ITEM_HEIGHT,
        itemSpacing = DEFAULT_ITEM_SPACING,
        children,
    } = props;

    const ref = React.useRef<HTMLDivElement>(null);
    const [rect] = useElementSizeTracker(ref);

    // if spacing > 0, we need to create separate shimmer items, since otherwise there's an awkward shimmering
    // blank space between each item
    const numElementsPerShimmer = itemSpacing > 0 ? 1 : numShimmerElements;
    const numShimmersToCreate =
        numShimmerElements > 1 && itemSpacing > 0 && !isDataLoaded ? numShimmerElements : 1;

    const totalWidth = rect?.width || defaultTotalWidth;
    const shimmerElements: JSX.Element[] = React.useMemo(
        () => renderShimmerItems(totalWidth, numElementsPerShimmer, itemHeight),
        [totalWidth, numElementsPerShimmer, itemHeight]
    );

    const getShimmerStyles = React.useCallback(
        (props: IShimmerProps): IShimmerStyles => {
            return {
                dataWrapper: {
                    height: 'auto',
                    display: 'flex',
                    flex: '1 1 auto',
                    width: '100%',
                },
                shimmerWrapper: mergeStyles(shimmerWrapperClassName, {
                    maxHeight: itemHeight * numElementsPerShimmer,
                }),
            };
        },
        [itemHeight, numElementsPerShimmer, shimmerWrapperClassName]
    );

    const content: JSX.Element[] = React.useMemo(() => {
        const elements: JSX.Element[] = [];
        for (let i = 0; i < numShimmersToCreate; i++) {
            elements.push(
                <Shimmer
                    key={i}
                    className={shimmerClassName}
                    styles={getShimmerStyles}
                    customElementsGroup={shimmerElements}
                    width={`${totalWidth}px`}
                    height={itemHeight}
                    isDataLoaded={isDataLoaded}>
                    {i == 0 ? children : null}
                </Shimmer>
            );
        }
        return elements;
    }, [
        numShimmersToCreate,
        shimmerClassName,
        getShimmerStyles,
        shimmerElements,
        totalWidth,
        itemHeight,
        isDataLoaded,
        children,
    ]);

    return (
        <div ref={ref} className={classNames(className || '', styles.wrapper)}>
            {content}
        </div>
    );
};

/**
 * Renders shimmer elements. For each shimmer element, block 1 is the free busy bar, block 2 is the persona placeholder, and both
 * of these remain the same size regardless of total width. Block 3 for text and 4 for the spacer maintain the ratio of
 * thirdBlockWidth / fourthBlockWidth, but the total size is scaled by remaining available space after the placeholder.
 * @param totalWidth the width of the entire item
 */
function renderShimmerItems(
    totalWidth: number,
    numShimmerElements: number,
    itemHeight: number
): JSX.Element[] {
    let shimmerElements: JSX.Element[] = [];
    const remainingSpace = totalWidth - firstBlockWidth - secondBlockWidth;
    const thirdScaled =
        remainingSpace > 0
            ? Math.floor(thirdBlockWidth * (remainingSpace / (thirdBlockWidth + fourthBlockWidth)))
            : thirdBlockWidth;
    const fourthScaled = remainingSpace > 0 ? remainingSpace - thirdScaled : fourthBlockWidth;

    const itemStyles = {
        height: itemHeight,
    };

    // lines have a border of 2px on top and 1px on bottom applied to them, so subtract off 3px;
    const lineHeights = itemHeight / 4 - 3;

    for (let i = 0; i < numShimmerElements; i++) {
        shimmerElements.push(
            <div
                style={itemStyles}
                className={styles.shimmerItemWrapper}
                key={'agendaEventsLoadingView_' + i}>
                <ShimmerElementsGroup
                    shimmerElements={[
                        { type: ElemType.gap, width: firstBlockWidth / 2, height: itemHeight },
                        { type: ElemType.line, width: 6, height: 46 },
                        {
                            type: ElemType.gap,
                            width: 10 /* firstBlockWidth / 2 - the line width */,
                            height: itemHeight,
                        },
                    ]}
                />
                <ShimmerElementsGroup
                    flexWrap={true}
                    width={`${secondBlockWidth}px`}
                    shimmerElements={[
                        { type: ElemType.gap, width: secondBlockWidth, height: itemHeight / 4 },
                        { type: ElemType.line, width: 37, height: lineHeights },
                        {
                            type: ElemType.gap,
                            width: 28 /* secondBlockWidth - line width */,
                            height: itemHeight / 4,
                        },
                        { type: ElemType.gap, width: secondBlockWidth, height: itemHeight / 4 },
                        { type: ElemType.gap, width: secondBlockWidth, height: itemHeight / 4 },
                    ]}
                />
                <ShimmerElementsGroup
                    flexWrap={true}
                    width={`${thirdScaled}px`}
                    shimmerElements={[
                        { type: ElemType.gap, width: thirdScaled, height: itemHeight / 4 },
                        { type: ElemType.line, width: thirdScaled, height: lineHeights },
                        { type: ElemType.line, width: thirdScaled * 0.6, height: lineHeights },
                        {
                            type: ElemType.gap,
                            width: thirdScaled * 0.4,
                            height: itemHeight / 4,
                        },
                        { type: ElemType.gap, width: thirdScaled, height: itemHeight / 4 },
                    ]}
                />
                <ShimmerElementsGroup
                    width={`${fourthScaled}px`}
                    shimmerElements={[
                        { type: ElemType.gap, width: fourthScaled, height: itemHeight },
                    ]}
                />
            </div>
        );
    }
    return shimmerElements;
}
